import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { App, Button, Card, Form, InputNumber, Modal, Space, Table, Tag } from 'antd'
import { EditOutlined, FilterOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { sellerInventoryAPI } from '../utils/sellerApi'
import { formatSellerCurrency, useSellerCurrencyStore } from '../utils/currency'
import { useSellerAuth } from '../contexts/SellerAuthContext'

interface InventoryItem {
  id: number
  product_id: number
  product_name: string
  variation_name: string
  sku: string
  price: number
  current_stock: number
  slug: string
}

interface InventorySummary {
  in_stock: number
  low_stock: number
  out_stock: number
  total_items: number
  total_skus: number
}

type UnknownRecord = Record<string, unknown>

type InventoryChartRow = {
  label: string
  stock: number
  products: number
}

const chartBoxStyle: React.CSSProperties = {
  width: '100%',
  height: 340,
  minWidth: 0,
  overflowX: 'auto',
  overflowY: 'hidden',
}

const CHART_WIDTH = 920
const CHART_HEIGHT = 320
const CHART_PADDING = {
  top: 24,
  right: 28,
  bottom: 64,
  left: 48,
}

const AXIS_COLOR = '#d7e2df'
const GRID_COLOR = '#edf3f2'
const TEXT_COLOR = '#6b7d79'
const STOCK_COLOR = '#0f766e'
const PRODUCT_COLOR = '#0ea5e9'
const TOOLTIP_WIDTH = 168
const TOOLTIP_HEIGHT = 74

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max)

const shortenLabel = (value: string, maxLength = 16) => {
  const label = value.trim()
  return label.length > maxLength ? `${label.slice(0, maxLength - 2)}..` : label
}

const isObject = (value: unknown): value is UnknownRecord =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const normalizeNumber = (value: unknown): number => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const normalizeLabel = (value: unknown, fallback: string): string => {
  if (typeof value === 'string' && value.trim().length > 0) {
    return value
  }

  if (typeof value === 'number') {
    return String(value)
  }

  return fallback
}

const toChartRowsFromObjectArray = (rows: UnknownRecord[]): InventoryChartRow[] =>
  rows.map((entry, index) => ({
    label: normalizeLabel(entry.name ?? entry.label ?? entry.category, `Category ${index + 1}`),
    stock: normalizeNumber(entry.total_stock ?? entry.stock ?? entry.value),
    products: normalizeNumber(entry.product_count ?? entry.products ?? entry.count),
  }))

const normalizeInventoryAnalysis = (payload: unknown): InventoryChartRow[] => {
  if (Array.isArray(payload)) {
    if (payload.every((entry) => isObject(entry))) {
      return toChartRowsFromObjectArray(payload as UnknownRecord[])
    }

    return payload.map((entry, index) => ({
      label: `Category ${index + 1}`,
      stock: normalizeNumber(entry),
      products: 0,
    }))
  }

  if (!isObject(payload)) {
    return []
  }

  const labels = Array.isArray(payload.labels) ? payload.labels : []
  const datasets = Array.isArray(payload.datasets) ? payload.datasets : []

  if (labels.length > 0 && datasets.length > 0) {
    const stockData = Array.isArray((datasets[0] as UnknownRecord)?.data)
      ? ((datasets[0] as UnknownRecord).data as unknown[])
      : []
    const productsData = Array.isArray((datasets[1] as UnknownRecord)?.data)
      ? ((datasets[1] as UnknownRecord).data as unknown[])
      : []

    return labels.map((label, index) => ({
      label: normalizeLabel(label, `Category ${index + 1}`),
      stock: normalizeNumber(stockData[index]),
      products: normalizeNumber(productsData[index]),
    }))
  }

  if (Array.isArray(payload.data)) {
    const dataRows = payload.data as unknown[]
    if (dataRows.every((entry) => isObject(entry))) {
      return toChartRowsFromObjectArray(dataRows as UnknownRecord[])
    }

    return dataRows.map((entry, index) => ({
      label: `Category ${index + 1}`,
      stock: normalizeNumber(entry),
      products: 0,
    }))
  }

  const objectEntries = Object.entries(payload).filter(
    ([key, value]) => !['labels', 'datasets', 'data'].includes(key) && (typeof value === 'number' || typeof value === 'string'),
  )

  if (objectEntries.length > 0) {
    return objectEntries.map(([key, value], index) => ({
      label: normalizeLabel(key, `Category ${index + 1}`),
      stock: normalizeNumber(value),
      products: 0,
    }))
  }

  return []
}

const ensureChartSignal = (rows: InventoryChartRow[], totalItems: number, totalSkus: number): InventoryChartRow[] => {
  if (rows.length === 0 && totalItems > 0) {
    return [{ label: 'Current', stock: totalItems, products: totalSkus }]
  }

  const hasPositiveValue = rows.some((row) => row.stock > 0 || row.products > 0)
  if (!hasPositiveValue && (totalItems > 0 || totalSkus > 0)) {
    return rows.map((row, index) => ({
      ...row,
      stock: index === rows.length - 1 ? totalItems : 0,
      products: index === rows.length - 1 ? totalSkus : 0,
    }))
  }

  return rows
}

const formatCurrency = (value: number) =>
  formatSellerCurrency(Number(value ?? 0), { fromInr: true })

const InventoryBarChart = ({ rows }: { rows: InventoryChartRow[] }) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)

  if (rows.length === 0) {
    return (
      <div
        style={{
          height: '100%',
          display: 'grid',
          placeItems: 'center',
          color: TEXT_COLOR,
          fontSize: 13,
        }}
      >
        No inventory analysis data available
      </div>
    )
  }

  const dynamicChartWidth = Math.max(CHART_WIDTH, rows.length * 118 + CHART_PADDING.left + CHART_PADDING.right)
  const baselineY = CHART_HEIGHT - CHART_PADDING.bottom
  const maxValue = Math.max(
    ...rows.flatMap((row) => [row.stock, row.products]),
    1,
  )
  const chartWidth = dynamicChartWidth - CHART_PADDING.left - CHART_PADDING.right
  const chartHeight = baselineY - CHART_PADDING.top
  const slot = chartWidth / rows.length
  const groupWidth = Math.min(82, slot * 0.72)
  const gap = Math.max(5, groupWidth * 0.1)
  const barWidth = Math.max(7, (groupWidth - gap) / 2)

  const yGridRatios = [0.25, 0.5, 0.75, 1]

  return (
    <div style={{ height: '100%', minWidth: dynamicChartWidth, display: 'grid', gridTemplateRows: 'minmax(0, 1fr) auto', gap: 10 }}>
      <svg
        viewBox={`0 0 ${dynamicChartWidth} ${CHART_HEIGHT}`}
        width="100%"
        height="100%"
        role="img"
        aria-label="Inventory category chart"
      >
        <line
          x1={CHART_PADDING.left}
          y1={baselineY}
          x2={dynamicChartWidth - CHART_PADDING.right}
          y2={baselineY}
          stroke={AXIS_COLOR}
          strokeWidth={1}
        />
        <line
          x1={CHART_PADDING.left}
          y1={CHART_PADDING.top}
          x2={CHART_PADDING.left}
          y2={baselineY}
          stroke={AXIS_COLOR}
          strokeWidth={1}
        />

        {yGridRatios.map((ratio, index) => {
          const y = baselineY - ratio * chartHeight
          const value = Math.round(maxValue * ratio)
          return (
            <g key={`grid-${index}`}>
              <line
                x1={CHART_PADDING.left}
                y1={y}
                x2={dynamicChartWidth - CHART_PADDING.right}
                y2={y}
                stroke={GRID_COLOR}
                strokeWidth={1}
                strokeDasharray="4 4"
              />
              <text x={CHART_PADDING.left - 8} y={y + 4} textAnchor="end" fill={TEXT_COLOR} fontSize={11}>
                {value}
              </text>
            </g>
          )
        })}

        {rows.map((row, index) => {
          const centerX = CHART_PADDING.left + slot * index + slot / 2
          const groupStartX = centerX - groupWidth / 2

          const stockHeight = (row.stock / maxValue) * chartHeight
          const productsHeight = (row.products / maxValue) * chartHeight

          const stockY = baselineY - stockHeight
          const productsY = baselineY - productsHeight

          const label = shortenLabel(row.label)
          const isHovered = hoveredIndex === index
          const isDimmed = hoveredIndex !== null && !isHovered

          return (
            <g key={`${row.label}-${index}`}>
              <rect
                x={groupStartX}
                y={stockY}
                width={barWidth}
                height={Math.max(stockHeight, 2)}
                rx={6}
                fill={STOCK_COLOR}
                opacity={isDimmed ? 0.34 : 0.92}
                style={{ transition: 'opacity 140ms ease' }}
              />
              <rect
                x={groupStartX + barWidth + gap}
                y={productsY}
                width={barWidth}
                height={Math.max(productsHeight, 2)}
                rx={6}
                fill={PRODUCT_COLOR}
                opacity={isDimmed ? 0.34 : 0.92}
                style={{ transition: 'opacity 140ms ease' }}
              />

              <text
                x={centerX}
                y={CHART_HEIGHT - 28}
                textAnchor="middle"
                fill={isHovered ? '#12211b' : TEXT_COLOR}
                fontSize={11}
                fontWeight={isHovered ? 800 : 400}
              >
                {label || `Category ${index + 1}`}
              </text>
              <rect
                x={centerX - slot / 2}
                y={CHART_PADDING.top}
                width={slot}
                height={baselineY - CHART_PADDING.top + 30}
                fill="transparent"
                tabIndex={0}
                role="button"
                aria-label={`${row.label}: ${row.stock} stock, ${row.products} products`}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
                onFocus={() => setHoveredIndex(index)}
                onBlur={() => setHoveredIndex(null)}
              />
            </g>
          )
        })}

        {hoveredIndex !== null ? (() => {
          const row = rows[hoveredIndex]
          const centerX = CHART_PADDING.left + slot * hoveredIndex + slot / 2
          const topValue = Math.max(row.stock, row.products)
          const topY = baselineY - (topValue / maxValue) * chartHeight
          const tooltipX = clamp(centerX - TOOLTIP_WIDTH / 2, CHART_PADDING.left, dynamicChartWidth - CHART_PADDING.right - TOOLTIP_WIDTH)
          const tooltipY = clamp(topY - TOOLTIP_HEIGHT - 14, 8, baselineY - TOOLTIP_HEIGHT - 8)

          return (
            <g pointerEvents="none">
              <rect
                x={tooltipX}
                y={tooltipY}
                width={TOOLTIP_WIDTH}
                height={TOOLTIP_HEIGHT}
                rx={8}
                fill="#ffffff"
                stroke="#dfe8e4"
                filter="drop-shadow(0 10px 18px rgba(15, 32, 26, 0.16))"
              />
              <text x={tooltipX + 12} y={tooltipY + 22} fill={TEXT_COLOR} fontSize={11} fontWeight={700}>
                {shortenLabel(row.label, 20)}
              </text>
              <text x={tooltipX + 12} y={tooltipY + 44} fill={STOCK_COLOR} fontSize={13} fontWeight={800}>
                Stock: {row.stock.toLocaleString()}
              </text>
              <text x={tooltipX + 12} y={tooltipY + 62} fill={PRODUCT_COLOR} fontSize={13} fontWeight={800}>
                Products: {row.products.toLocaleString()}
              </text>
            </g>
          )
        })() : null}
      </svg>

      <div style={{ display: 'flex', justifyContent: 'center', gap: 14, flexWrap: 'wrap', fontSize: 12, paddingBottom: 2 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#334155' }}>
          <span style={{ width: 10, height: 10, borderRadius: 999, backgroundColor: STOCK_COLOR }} />
          Stock
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#334155' }}>
          <span style={{ width: 10, height: 10, borderRadius: 999, backgroundColor: PRODUCT_COLOR }} />
          Products
        </span>
      </div>
    </div>
  )
}

const InventoryPage = () => {
  const activeCurrency = useSellerCurrencyStore((state) => state.currency)
  const [inventory, setInventory] = useState<InventoryItem[]>([])
  const [summary, setSummary] = useState<InventorySummary | null>(null)
  const [analysisRows, setAnalysisRows] = useState<InventoryChartRow[]>([])
  const [loading, setLoading] = useState(false)
  const [filterLowStock, setFilterLowStock] = useState(false)
  const [editingItem, setEditingItem] = useState<InventoryItem | null>(null)
  const [modalVisible, setModalVisible] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form] = Form.useForm<{ stock: number }>()
  const { hasPermission } = useSellerAuth()
  const { message } = App.useApp()

  const canUpdateStock = hasPermission('inventory.update')

  const fetchInventory = useCallback(async () => {
    setLoading(true)
    try {
      const response = await sellerInventoryAPI.list(filterLowStock ? { low_stock: true } : undefined)
      setInventory(response.data.data.data ?? [])
      setSummary(response.data.summary ?? null)
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to load inventory')
    } finally {
      setLoading(false)
    }
  }, [filterLowStock, message])

  const fetchAnalysis = useCallback(async () => {
    try {
      const response = await sellerInventoryAPI.analysis()
      const nextRows = normalizeInventoryAnalysis(response.data?.data ?? response.data ?? [])
      setAnalysisRows(nextRows)
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to load inventory analysis')
    }
  }, [message])

  useEffect(() => {
    void fetchInventory()
  }, [fetchInventory])

  useEffect(() => {
    void fetchAnalysis()
  }, [fetchAnalysis])

  const openModal = (item: InventoryItem) => {
    if (!canUpdateStock) {
      message.warning('You are not permitted to update inventory stock.')
      return
    }

    setEditingItem(item)
    form.setFieldsValue({ stock: item.current_stock })
    setModalVisible(true)
  }

  const updateStock = async (values: { stock: number }) => {
    if (!editingItem) {
      return
    }

    if (!canUpdateStock) {
      message.warning('You are not permitted to update inventory stock.')
      return
    }

    setSaving(true)
    try {
      await sellerInventoryAPI.updateStock(editingItem.product_id, editingItem.id, values.stock)
      message.success('Stock updated')
      setModalVisible(false)
      setEditingItem(null)
      await Promise.all([fetchInventory(), fetchAnalysis()])
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to update stock')
    } finally {
      setSaving(false)
    }
  }

  const columns: ColumnsType<InventoryItem> = [
    {
      title: 'Product',
      key: 'product_name',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 600 }}>{record.product_name}</div>
          <div style={{ color: '#64748b', fontSize: 12 }}>{record.variation_name || 'Default variation'}</div>
        </div>
      ),
    },
    { title: 'SKU', dataIndex: 'sku', key: 'sku' },
    {
      title: `Price (${activeCurrency})`,
      key: 'price',
      render: (_, record) => formatCurrency(Number(record.price ?? 0)),
    },
    {
      title: 'Stock',
      key: 'stock',
      render: (_, record) => <Tag color={record.current_stock <= 10 ? 'red' : 'green'}>{record.current_stock}</Tag>,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        canUpdateStock ? (
          <Button icon={<EditOutlined />} onClick={() => openModal(record)}>
            Update Stock
          </Button>
        ) : (
          <Tag color="default">Read only</Tag>
        )
      ),
    },
  ]

  const chartData = useMemo(
    () => ensureChartSignal(analysisRows, Number(summary?.total_items ?? 0), Number(summary?.total_skus ?? 0)),
    [analysisRows, summary?.total_items, summary?.total_skus],
  )

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 28 }}>Inventory Management</h1>
          <div style={{ color: '#64748b', marginTop: 6 }}>Watch low stock, update variation quantities, and review category inventory trends.</div>
        </div>
        <Button icon={<FilterOutlined />} type={filterLowStock ? 'primary' : 'default'} onClick={() => setFilterLowStock((value) => !value)}>
          {filterLowStock ? 'Show All Inventory' : 'Low Stock Only'}
        </Button>
      </div>

      <Space orientation="vertical" size="large" style={{ width: '100%' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
          <Card title="In Stock">{summary?.in_stock ?? 0}</Card>
          <Card title="Low Stock">{summary?.low_stock ?? 0}</Card>
          <Card title="Out of Stock">{summary?.out_stock ?? 0}</Card>
          <Card title="Total Items">{summary?.total_items ?? 0}</Card>
        </div>

        <Card title="Inventory by Category">
          <div style={chartBoxStyle}>
            <InventoryBarChart rows={chartData} />
          </div>
        </Card>

        <Table columns={columns} dataSource={inventory} rowKey="id" loading={loading} pagination={{ pageSize: 12 }} scroll={{ x: 860 }} />
      </Space>

      <Modal title="Update Stock" open={modalVisible} onCancel={() => setModalVisible(false)} footer={null}>
        <Form form={form} layout="vertical" onFinish={updateStock}>
          <Form.Item label="Variation">
            <div>{editingItem?.product_name} {editingItem?.variation_name ? `- ${editingItem.variation_name}` : ''}</div>
          </Form.Item>
          <Form.Item name="stock" label="New Stock Quantity" rules={[{ required: true, message: 'Stock is required' }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
            <Button onClick={() => setModalVisible(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={saving}>
              Save Stock
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  )
}

export default InventoryPage
